// libs/js/shared/contexts/mapAnalytics.js
/**
 * @typedef {Object} ViewChangedData
 * @property {'building' | 'end_zone'} view_type - The selected view type
 */

/**
 * @typedef {Object} DatasetChangedData
 * @property {'semester' | 'hourly'} dataset - The selected dataset type
 */

/**
 * @typedef {Object} MetricChangedData
 * @property {string} metric_id - The metric identifier
 * @property {string} metric_name - The metric display name
 * @property {'semester' | 'hourly'} dataset - Which dataset the metric belongs to
 */

/**
 * @typedef {Object} FilterAppliedData
 * @property {'buildings' | 'floors' | 'usages'} filter_type - The type of filter
 * @property {string[]} values - The selected filter values
 */

/**
 * @typedef {Object} SearchPerformedData
 * @property {string} query - The search query
 */

/**
 * @typedef {Object} ZoneSelectedData
 * @property {string} zone_id - The zone identifier
 * @property {string} zone_name - The zone display name
 * @property {string} zone_type - The zone type (building, floor, room, etc.)
 * @property {'map' | 'list'} source - Where the selection originated
 */

/**
 * @typedef {Object} DaysChangedData
 * @property {number[]} days - Array of day values (0-6)
 * @property {string[]} day_labels - Array of day abbreviations
 * @property {number} day_count - Number of days selected
 */

/**
 * @typedef {Object} HourChangedData
 * @property {number} hour - The hour value (0-23)
 * @property {string} hour_label - The hour display label (e.g., "2pm")
 */

/**
 * @typedef {Object} PlaybackStartedData
 * @property {number} speed - The playback speed multiplier
 */

/**
 * @typedef {Object} PlaybackSpeedChangedData
 * @property {number} speed - The new playback speed
 * @property {number} previous_speed - The previous playback speed
 */

/**
 * @typedef {Object} GraphOpenedData
 * @property {string} zone_id - The zone identifier
 * @property {string} zone_name - The zone display name
 * @property {string} zone_type - The zone type
 */

/**
 * @typedef {Object} GraphTypeChangedData
 * @property {'day' | 'hour'} graph_type - The selected graph type
 */

/**
 * @typedef {Object} CampusFlowSessionStartData
 * @property {string} session_id - Unique identifier for this session
 */

/**
 * @typedef {Object} CampusFlowInteractionData
 * @property {string} interaction_type - Type of interaction (e.g., "threshold_change")
 * @property {string} session_id - Session this interaction belongs to
 */

let hasTrackedMapViewInSession = false

export const _resetMapViewTracking = () => {
  hasTrackedMapViewInSession = false
}

/**
 * Creates the simple event-forwarding tracker methods.
 * Each tracker calls capture with a fixed event name and passes data through.
 *
 * @param {Function} capture - The capture function from AnalyticsContext
 * @returns {Object} Simple tracker methods
 */
const createSimpleTrackers = capture => ({
  /** @param {ViewChangedData} data */
  trackViewChanged: data => capture("map_view_changed", data),
  /** @param {DatasetChangedData} data */
  trackDatasetChanged: data => capture("map_dataset_changed", data),
  /** @param {MetricChangedData} data */
  trackMetricChanged: data => capture("map_metric_changed", data),
  /** @param {FilterAppliedData} data */
  trackFilterApplied: data => capture("map_filter_applied", data),
  /** @param {SearchPerformedData} data */
  trackSearchPerformed: data => capture("map_search_performed", data),
  /** @param {ZoneSelectedData} data */
  trackZoneSelected: data => capture("map_zone_selected", data),
  /** @param {DaysChangedData} data */
  trackDaysChanged: data => capture("map_days_changed", data),
  /** @param {HourChangedData} data */
  trackHourChanged: data => capture("map_hour_changed", data),
  /** @param {PlaybackStartedData} data */
  trackPlaybackStarted: data => capture("map_playback_started", data),
  /** @param {PlaybackSpeedChangedData} data */
  trackPlaybackSpeedChanged: data => capture("map_playback_speed_changed", data),
  /** @param {GraphOpenedData} data */
  trackGraphOpened: data => capture("map_graph_opened", data),
  /** @param {GraphTypeChangedData} data */
  trackGraphTypeChanged: data => capture("map_graph_type_changed", data),
  /** @param {CampusFlowSessionStartData} data */
  trackCampusFlowSessionStart: data => capture("campus_flow_session_start", data),
  /** @param {CampusFlowInteractionData} data */
  trackCampusFlowInteraction: data => capture("campus_flow_interaction", data),
})

/**
 * Creates map analytics tracking methods and EventBus listeners.
 *
 * @param {Object} options
 * @param {Function} options.capture - The capture function from AnalyticsContext
 * @param {Object} options.eventBus - The eventBus instance for listening to events
 * @param {Function} [options.getPersonProperty] - Gets a PostHog person property by key
 * @returns {Object} Map analytics tracking methods and registerListeners function
 */
export const createMapAnalytics = ({ capture, eventBus, getPersonProperty = () => undefined }) => {
  const simpleTrackers = createSimpleTrackers(capture)

  const trackMapViewLoaded = () => {
    const hasVisitedMapView = !!getPersonProperty("has_visited_map_view")
    const isFirstVisit = !hasTrackedMapViewInSession && !hasVisitedMapView
    hasTrackedMapViewInSession = true

    capture("map_view_loaded", {
      is_first_visit: isFirstVisit,
      $set_once: { has_visited_map_view: true },
    })
  }

  /**
   * Registers EventBus listeners for map analytics events.
   * @returns {Function} Cleanup function to unregister listeners
   */
  const registerListeners = () => {
    const handleZoneSelected = data => {
      simpleTrackers.trackZoneSelected({
        zone_id: data.id,
        zone_name: data.name || null,
        zone_type: data.type || null,
        source: data.source,
      })
    }

    eventBus.on("listview-resource-selected", handleZoneSelected)

    return () => {
      eventBus.off("listview-resource-selected", handleZoneSelected)
    }
  }

  return {
    ...simpleTrackers,
    trackMapViewLoaded,
    registerListeners,
    reset: _resetMapViewTracking,
  }
}
